import { useRef, useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// @Utility
import { getStorage, removeStorage } from "./../../../../../../@utility/app/storage";
import { normalizeAffiliate } from "./../../../../../../@utility/app/affiliate";
import alertText from "./../../../../../../@utility/app/alertText";
import validateInput from "./../../../../../../@utility/app/validateInput";

// Redux
import { setHomeData } from "./../../../../../../@core/redux/actions/home/index";
import { setStatusSpinner } from "./../../../../../../@core/redux/actions/app/spinner";
import { setDataPopupContact } from "./../../../../../../@core/redux/actions/app/popup";

// Services
import {
    registerShort as registerApp,
    registerOtpRequest,
    registerOtpResend,
    registerOtpVerify
} from "./../../../../../../@core/auth/jwt/jwtService";
import { loadHomeData } from "./../../../../../../@core/services/homeService";

//  CSS
import formStyles from "./../../../assets/css/Input.module.css";
import buttonStyles from "./../../../assets/css/Button.module.css";

// Icon
import PhoneIcon from '@material-ui/icons/Phone';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import PersonIcon from '@material-ui/icons/Person';

// Component
import SnackbarSet from "./../../SnackbarSet";
import OtpVerifyApp from "./../OtpVerify";

const OTP_RESEND_COOLDOWN = 60; // server กันขอรหัสถี่กว่านี้ (SmsOtpResendCooldown) — ตอบ code 93
const MAX_RESEND = 3;   // ส่งซ้ำได้ 3 ครั้งต่อ ref
const MAX_WRONG = 3;    // กรอกผิดครบ 3 ครั้ง เบอร์โดนระงับ 1 ชม. (API ไม่ส่งจำนวนที่เหลือมา ต้องนับเอง)
const RATE_LIMIT_DELAY = 1200;  // rate limit 1 req/วินาที ต่อ token/IP — เว้นระยะก่อนยิงเส้นถัดไป
const MAX_CHAIN_HOPS = 3;       // เพดานการต่อเส้นอัตโนมัติต่อการกดสมัคร 1 ครั้ง

// code ที่ระบบไม่เผา OTP ทิ้ง → กลับไปแก้ข้อมูลแล้วยืนยันด้วย ref+otp เดิมได้เลย
const KEEP_OTP_CODES = [14, 51, 67, 68, 37];
// code ที่ ref ใช้ไม่ได้อีกแล้ว → กลับไปเริ่มขอ OTP ใหม่
const DEAD_REF_CODES = [89, 97];

const nowInSeconds = () => Math.floor(Date.now() / 1000);

export default function Register01App(props) {
    const { register, handleSubmit, formState: { errors } } = useForm();
    const childRef = useRef();
    const dispatch = useDispatch();
    const [selectBank, setSelectBank] = useState(null);
    const [showAccName, setShowAccName] = useState(false);

    // ---- OTP ----
    const [otpInfo, setOtpInfo] = useState(null);       // { ref, phone, expireAt, lastSentAt }
    const [showOtp, setShowOtp] = useState(false);
    const [resendCount, setResendCount] = useState(0);
    const [wrongCount, setWrongCount] = useState(0);
    const [clearSignal, setClearSignal] = useState(0);
    const [formData, setFormData] = useState(null);
    // ยิง register-otp/request เป็นเส้นแรกเสมอ ไม่ต้องเดาจาก setting ที่อาจค้าง cache
    // null = ยังไม่รู้ · false = server ตอบ 95 มาแล้ว (agent ปิด OTP) → รอบต่อไปข้ามไป register-short เลย
    const [otpSupported, setOtpSupported] = useState(null);
    // เวลาที่ขอรหัสใหม่ได้ (unix seconds) — backend กัน 60 วิ นับจาก SMS ใบล่าสุดของเบอร์นั้น
    // ค่านี้ต้องรอดจาก resetOtpFlow เพราะ cooldown ผูกกับเบอร์ที่ server ไม่ใช่รอบการกรอก
    const [cooldownUntil, setCooldownUntil] = useState(0);
    const [tick, setTick] = useState(() => nowInSeconds());

    const otpStillValid = otpInfo !== null && otpInfo.expireAt > nowInSeconds();
    const cooldownLeft = Math.max(0, cooldownUntil - tick);

    // เดินนาฬิกาเฉพาะตอนที่ยังติด cooldown อยู่ — พ้นแล้วหยุดเอง ไม่ปล่อย interval ค้าง
    useEffect(() => {
        if (cooldownUntil === 0) {
            return undefined;
        }

        const timer = setInterval(() => setTick(nowInSeconds()), 1000);
        return () => clearInterval(timer);
    }, [cooldownUntil]);

    // บอก modal ว่ากำลังอยู่ในขั้นตอน OTP เพื่อให้ถามยืนยันก่อนปิด / ก่อนกดย้อนกลับ
    // เก็บ callback ไว้ใน ref เพราะ props เปลี่ยน object ทุก render
    const onOtpProcessRef = useRef(props.onOtpProcess);
    onOtpProcessRef.current = props.onOtpProcess;

    useEffect(() => {
        if (onOtpProcessRef.current) {
            onOtpProcessRef.current(showOtp);
        }
    }, [showOtp]);

    useEffect(() => {
        return () => {
            if (onOtpProcessRef.current) {
                onOtpProcessRef.current(false);
            }
        };
    }, []);

    // ยิงเส้นถัดไปแบบเว้นระยะกัน rate limit — คา spinner ไว้ระหว่างรอ จะได้ไม่มีจังหวะว่างให้กดซ้ำ
    const chainTimerRef = useRef(null);
    const chainHopsRef = useRef(0);

    useEffect(() => {
        return () => {
            if (chainTimerRef.current !== null) {
                clearTimeout(chainTimerRef.current);
                dispatch(setStatusSpinner({ status: false, textProcess: null }));
            }
        };
    }, [dispatch]);

    // const snackbar = (da) => {
    //     childRef.current.snackbar(da);
    // }

    const alertWarning = (text) => {
        dispatch(props.setDataAlert({
            type: "warning",
            title: "แจ้งเตือน",
            text: text,
            action: true,
            redirect: "",
            show: true,
            buttonText: "ตกลง"
        }));
    };

    const alertByCode = (statusCode) => {
        let dataAlt = alertText(statusCode);
        dispatch(props.setDataAlert({
            type: dataAlt.statusAlert,
            title: dataAlt.titleAlert,
            text: dataAlt.textAlert,
            action: true,
            redirect: "",
            show: true,
            buttonText: "ตกลง"
        }));
    };

    // ตรวจข้อมูลฟอร์มแล้วประกอบ payload — คืน null ถ้าไม่ผ่าน (เด้ง alert ให้แล้ว)
    // ใช้ร่วมกันทั้งเส้น register-short และ register-otp/vertify
    const buildRegisterPayload = (data) => {
        if (selectBank === null) {
            alertWarning("กรุณาเลือกธนาคารที่ต้องการทำรายการ !");
            return null;
        }

        if (data.phone === "" || !data.bank_account) {
            alertWarning("กรุณากรอกข้อมูล เบอร์โทรศัพท์ และเลขบัญชีธนาคาร !");
            return null;
        }

        let dataBankAccountName = "";
        if (data?.bank_account_name !== undefined) {
            if (data?.bank_account_name === "") {
                alertWarning("กรุณากรอกข้อมูลชื่อบัญชีธนาคาร !");
                return null;
            }

            dataBankAccountName = data?.bank_account_name;
        }

        let myJson = {
            "username": data.phone,
            "password": "",
            "affiliate": getStorage("affiliate") === null ? "" : getStorage("affiliate"),
            /* ลิงก์เชิญ partner ใช้คนละ key กับ affiliate — backend ตัด prefix เอง
               โดยเทียบกับ agent code จริง จึงส่งค่าดิบไปตรง ๆ */
            "partner": getStorage("partner") ? getStorage("partner") : "",
            "refer": getStorage("refer") === null ? "" : getStorage("refer"),
            "know_web": "",
            "allusion": getStorage("allusion"),
            "bank_account": data.bank_account,
            "bank_name": dataBankAccountName,
            "bank_swift": selectBank,
            "fbc": getStorage("fbclid") === null ? "" : getStorage("fbclid"),
            "fbc_timestamp": getStorage("fbctimestamp") === null ? "" : getStorage("fbctimestamp"),
            "keitaro_id": (getStorage("clickid") && !getStorage("platform")) ? getStorage("clickid") : "",
            "voluum_c_id": (getStorage("clickid") && (getStorage("platform") === "voluum")) ? getStorage("clickid") : ""
        }

        if (getStorage("market") !== null && getStorage("market") !== "Null" && getStorage("market") !== "null" && getStorage("market") !== "") {
            myJson.refer = getStorage("market")
        }

        // console.log("test : ", (getStorage("clickid") && (getStorage("platform") === "voluum")));
        // console.log("myJson : ", myJson);
        // console.log("clickid : ", getStorage("clickid"));
        // console.log("platform : ", getStorage("platform"));

        return myJson;
    };

    // จัดการผลลัพธ์หลังสมัครสำเร็จ (ใช้ร่วมกันทั้ง 2 เส้น)
    const registerSuccess = (password) => {
        // สมัครสมาชิกสำเร็จ = จบขั้นตอน OTP แล้ว ต้องปลดธงก่อนสั่งปิด modal
        // ไม่งั้น AuthApp จะนึกว่ายังค้างอยู่หน้ากรอกรหัส แล้วเด้งถามยืนยันออกทั้งที่สมัครผ่านไปแล้ว
        if (onOtpProcessRef.current) {
            onOtpProcessRef.current(false);
        }

        setShowOtp(false);

        reloadHomeData();
        dispatch(props.setDataAlert({
            type: "success",
            title: password === null ? "แจ้งเตือน" : "สมัครสมาชิกสำเร็จ",
            text: password === null ? "สมัครสมาชิกสำเร็จ !" : `รหัสผ่านของคุณคือ : ${password}`,
            action: true,
            redirect: "",
            show: true,
            buttonText: "ตกลง"
        }));

        dispatch(setDataPopupContact(true));
        props.onShow(false)
        // removeStorage("refer");
        // removeStorage("affiliate");
        removeStorage("allusion");
    };

    // ยิงเส้นถัดไปแบบเว้นระยะกัน rate limit — คา spinner ไว้ระหว่างรอ จะได้ไม่มีจังหวะว่างให้กดซ้ำ
    const chainRequest = (run) => {
        // กัน ping-pong ถ้า agent สลับค่า customer_vertify_otp กลางคัน (95 ↔ 94 วนไม่จบ)
        if (chainHopsRef.current >= MAX_CHAIN_HOPS) {
            dispatch(setStatusSpinner({ status: false, textProcess: null }));
            alertWarning("เริ่มขั้นตอนสมัครไม่สำเร็จ กรุณาลองใหม่");
            return;
        }

        chainHopsRef.current += 1;
        dispatch(setStatusSpinner({ status: true, textProcess: "ระบบกำลังตรวจสอบข้อมูล กรุณารอสักครู่..." }));
        chainTimerRef.current = setTimeout(run, RATE_LIMIT_DELAY);
    };

    // นับถอยหลังจาก expire_in โดยอิงนาฬิกาเครื่องผู้ใช้ กันเวลาเครื่องกับ server ไม่ตรงกัน
    const resolveExpireAt = (result) => {
        if (result?.expire_in) {
            return nowInSeconds() + Number(result.expire_in);
        }

        return result?.expire_at || 0;
    };

    // สลับหน้าฟอร์ม ↔ หน้ากรอก OTP
    // ขยับ step ไปด้วย เพราะ Auth/index.js ซ่อนปุ่ม "เข้าสู่ระบบ / สมัครสมาชิก" เมื่อ step ไม่ใช่ 1
    const toggleOtpScreen = (show) => {
        setShowOtp(show);

        if (props.onStep) {
            props.onStep(show ? 2 : 1);
        }
    };

    // ทุกครั้งที่ SMS ถูกส่งจริง เริ่มนับ cooldown 60 วิ ให้ตรงกับฝั่ง server
    const markSmsSent = () => {
        setCooldownUntil(nowInSeconds() + OTP_RESEND_COOLDOWN);
        setTick(nowInSeconds());
    };

    // กลับไปหน้าฟอร์ม พร้อมทิ้ง ref เดิม (ต้องขอ OTP ใหม่)
    const resetOtp = () => {
        setOtpInfo(null);
        toggleOtpScreen(false);
        setResendCount(0);
        setWrongCount(0);
    };

    // ออกจากขั้นตอนยืนยัน OTP ไม่ว่าด้วยเหตุใด = เริ่มใหม่ทั้งกระบวนการ
    // ทิ้ง ref เดิม ตัวนับ และผลที่จำไว้จากรอบก่อนให้หมด กดสมัครครั้งถัดไปต้องขอรหัสใหม่เสมอ
    const resetOtpFlow = () => {
        if (chainTimerRef.current !== null) {
            clearTimeout(chainTimerRef.current);
            chainTimerRef.current = null;
            dispatch(setStatusSpinner({ status: false, textProcess: null }));
        }

        chainHopsRef.current = 0;
        resetOtp();
        setFormData(null);
        setOtpSupported(null);
    };

    const resetOtpFlowRef = useRef(null);
    resetOtpFlowRef.current = resetOtpFlow;

    // ปุ่มย้อนกลับของเบราว์เซอร์ระหว่างค้างอยู่หน้า OTP — modal ไม่ได้ผูกกับ route จึงไม่ปิดเอง
    useEffect(() => {
        const onPopState = () => {
            if (resetOtpFlowRef.current !== null) {
                resetOtpFlowRef.current();
            }
        };

        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, []);

    // ปุ่มย้อนกลับบนหัว modal (ModalFull โชว์เมื่อ step > 1) — มันแค่ลด step ไม่รู้จักสถานะ OTP
    // จับจังหวะ step เปลี่ยนจาก 2 กลับมา 1 แล้วล้างให้ (เทียบกับค่าก่อนหน้า ไม่งั้นตอนเปิดหน้า OTP จะโดนล้างเอง)
    const prevStepRef = useRef(props.step);

    useEffect(() => {
        const prevStep = prevStepRef.current;
        prevStepRef.current = props.step;

        if (prevStep > 1 && props.step === 1 && resetOtpFlowRef.current !== null) {
            resetOtpFlowRef.current();
        }
    }, [props.step]);

    const openOtpScreen = (result, phoneFallback) => {
        setOtpInfo({
            ref: result?.ref,
            phone: result?.phone || phoneFallback,
            expireAt: resolveExpireAt(result),
            lastSentAt: nowInSeconds()
        });
        markSmsSent();
        setResendCount(0);
        setWrongCount(0);
        setClearSignal(value => value + 1);
        toggleOtpScreen(true);
    };

    // สมัครแบบไม่ผ่าน OTP — retried กันเคสโดน rate limit ตอนต่อท้าย request ที่เพิ่งตอบ 95
    const registerShortFlow = (data, retried) => {
        const myJson = buildRegisterPayload(data);
        if (myJson === null) {
            return;
        }

        dispatch(setStatusSpinner({ status: true, textProcess: "ระบบกำลังตรวจสอบข้อมูล กรุณารอสักครู่..." }));

        registerApp(myJson).then((result) => {
            dispatch(setStatusSpinner({ status: false, textProcess: null }));
            const statusCode = result.code;

            if (statusCode === 200 || statusCode === 0) {
                registerSuccess(result.password);
                return;
            }

            // 94 = agent เปิด OTP แล้ว → ย้อนกลับไปเส้น OTP
            if (statusCode === 94) {
                setOtpSupported(null);
                chainRequest(() => startOtp(data));
                return;
            }

            if (statusCode === 429 && !retried) {
                chainRequest(() => registerShortFlow(data, true));
                return;
            }

            if (statusCode === 68) {
                setShowAccName(true);
            }

            alertByCode(statusCode);
        });
    };

    // ขอรหัส OTP ครั้งแรก — ตรวจฟอร์มให้ครบก่อน จะได้ไม่เปลือง SMS ตอนข้อมูลยังไม่พร้อม
    const startOtp = (data) => {
        const myJson = buildRegisterPayload(data);
        if (myJson === null) {
            return;
        }

        dispatch(setStatusSpinner({ status: true, textProcess: "กำลังส่งรหัส OTP กรุณารอสักครู่..." }));

        // ส่งข้อมูลสมัครไปให้ API ตรวจตั้งแต่เส้นนี้ จะได้ไม่เสีย SMS ฟรีตอน vertify ตอบ 14 / 51 / 68
        // ฝั่ง API ฟิลด์พวกนี้เป็นของเสริม (omitempty) — client ที่ยังส่งแค่ phone ก็ยังทำงานเหมือนเดิม
        registerOtpRequest({
            phone: data.phone,
            username: myJson.username,
            bank_account: myJson.bank_account,
            bank_name: myJson.bank_name,
            bank_swift: myJson.bank_swift
        }).then((response) => {
            dispatch(setStatusSpinner({ status: false, textProcess: null }));
            const statusCode = response.code;

            if (statusCode === 200 || statusCode === 0) {
                setOtpSupported(true);
                openOtpScreen(response.result, data.phone);
                return;
            }

            // 95 = agent ไม่ได้เปิด OTP → ถอยไปใช้เส้นสมัครปกติเงียบ ๆ (ยังไม่มี SMS ถูกส่ง)
            if (statusCode === 95) {
                setOtpSupported(false);
                chainRequest(() => registerShortFlow(data, false));
                return;
            }

            // 68 = ธนาคารยืนยันชื่อบัญชีไม่ได้ → เปิดช่องให้กรอกชื่อบัญชีเอง แล้วกดสมัครใหม่
            // เดิม code นี้โผล่ตอน vertify คือหลัง SMS ออกไปแล้ว ตอนนี้ API ตรวจให้ตั้งแต่ก่อนส่ง
            if (statusCode === 68) {
                setShowAccName(true);
            }

            // 66 เบอร์ผิด/ซ้ำ · 14 username ซ้ำ · 51 บัญชีซ้ำ · 93 ขอถี่ · 97 ถูกระงับ · 13 ส่ง SMS ไม่ออก
            // → อยู่หน้าฟอร์ม แก้ข้อมูลแล้วกดสมัครใหม่ได้ (alert ขึ้นให้จาก alertByCode)
            alertByCode(statusCode);
        });
    };

    const onSubmit = (data) => {
        setFormData(data);
        chainHopsRef.current = 0;

        // เคยเจอ 95 มาแล้วในรอบนี้ = agent ปิด OTP อยู่ ไม่ต้องยิง request ให้เสียเที่ยวอีก
        if (otpSupported === false) {
            registerShortFlow(data, false);
            return;
        }

        // ทุกครั้งที่กดสมัคร = เริ่มขั้นตอน OTP ใหม่ ไม่มีการใช้ ref เดิมต่อ
        setOtpInfo(null);
        startOtp(data);
    };

    // ยืนยัน OTP + สมัครสมาชิกในขั้นตอนเดียว
    const onSubmitOtp = (otp) => {
        const myJson = buildRegisterPayload(formData);
        if (myJson === null) {
            resetOtpFlow();
            return;
        }

        myJson.phone = formData.phone;
        myJson.ref = otpInfo?.ref;
        myJson.otp = otp;

        dispatch(setStatusSpinner({ status: true, textProcess: "ระบบกำลังตรวจสอบข้อมูล กรุณารอสักครู่..." }));

        registerOtpVerify(myJson).then((result) => {
            dispatch(setStatusSpinner({ status: false, textProcess: null }));
            const statusCode = result.code;

            if (statusCode === 200 || statusCode === 0) {
                registerSuccess(result.password);
                return;
            }

            // 92 = OTP ผิด → อยู่หน้าเดิม ให้กรอกใหม่ (นับโควตาเอง ครั้งที่ 3 API จะตอบ 97 แทน)
            if (statusCode === 92) {
                setWrongCount(value => value + 1);
                setClearSignal(value => value + 1);
                alertByCode(statusCode);
                return;
            }

            // 90 = OTP หมดอายุ → อยู่หน้าเดิม ให้กดส่งรหัสใหม่ (บังคับให้ตัวนับขึ้นหมดอายุทันที)
            if (statusCode === 90) {
                setOtpInfo(current => ({ ...current, expireAt: nowInSeconds() }));
                setClearSignal(value => value + 1);
                alertByCode(statusCode);
                return;
            }

            // 89 ไม่พบ ref / ใช้ไปแล้ว · 97 กรอกผิดครบ 3 ครั้ง เบอร์โดนระงับ → กลับหน้าฟอร์ม
            if (DEAD_REF_CODES.includes(statusCode)) {
                if (statusCode === 97) {
                    setWrongCount(MAX_WRONG);
                }
                resetOtpFlow();
                alertByCode(statusCode);
                return;
            }

            // ข้อมูลสมัครสมาชิกมีปัญหา → กลับไปแก้ที่ฟอร์ม แต่เก็บ ref+otp เดิมไว้ (OTP ยังไม่ถูกเผา)
            if (KEEP_OTP_CODES.includes(statusCode)) {
                if (statusCode === 68) {
                    setShowAccName(true);
                }

                resetOtpFlow();
                alertByCode(statusCode);
                return;
            }

            // 66 / 1 / 17 / 429 → อยู่หน้าเดิม ล้างช่องกรอก
            setClearSignal(value => value + 1);
            alertByCode(statusCode);
        });
    };

    // ขอรหัสซ้ำ — ใช้ ref เดิม รหัสเดิม แต่เวลาหมดอายุขยับใหม่
    const onResend = () => {
        dispatch(setStatusSpinner({ status: true, textProcess: "กำลังส่งรหัส OTP กรุณารอสักครู่..." }));

        registerOtpResend({ phone: formData?.phone, ref: otpInfo?.ref }).then((response) => {
            dispatch(setStatusSpinner({ status: false, textProcess: null }));
            const statusCode = response.code;

            if (statusCode === 200 || statusCode === 0) {
                // resend ใช้รหัสเดิม แค่ต่ออายุ — ไม่ล้างช่องกรอก และไม่ล้างโควตากรอกผิด
                setOtpInfo(current => ({
                    ...current,
                    phone: response.result?.phone || current?.phone,
                    expireAt: resolveExpireAt(response.result),
                    lastSentAt: nowInSeconds()
                }));
                setResendCount(value => value + 1);
                markSmsSent();
                return;
            }

            // 89 ไม่พบ ref · 97 เบอร์ถูกระงับ → กลับหน้าฟอร์ม
            if (DEAD_REF_CODES.includes(statusCode)) {
                resetOtpFlow();
                alertByCode(statusCode);
                return;
            }

            // 96 = ส่งซ้ำครบ 3 ครั้งแล้ว → ปิดปุ่มส่งซ้ำ ถ้ารหัสหมดอายุไปด้วยก็กลับหน้าฟอร์ม
            if (statusCode === 96) {
                setResendCount(MAX_RESEND);

                if (!otpStillValid) {
                    resetOtpFlow();
                }

                alertByCode(statusCode);
                return;
            }

            // 93 ยังไม่พ้น cooldown 60 วิ · 13 ส่ง SMS ไม่ออก → อยู่หน้าเดิม
            alertByCode(statusCode);
        });
    };

    // รหัสหมดอายุและส่งซ้ำครบ 3 ครั้งแล้ว — พากลับหน้าฟอร์มเพื่อเริ่มขอรหัสใหม่
    const onExhausted = () => {
        resetOtpFlow();
        alertWarning("รหัสหมดอายุและส่งซ้ำครบแล้ว กรุณาสมัครใหม่อีกครั้ง");
    };

    const clickBank = (data) => {
        setSelectBank(data);
    };

    const renderErrorPhone = () => {
        if (!errors.phone) return null;

        return (
            <div className="validateError validateTop">
                {errors.phone.message || "กรุณาระบุเบอร์โทรศัพท์ให้ถูกต้อง"}
            </div>
        );
    };

    const renderErrorBankAccount = () => {
        if (!errors.bank_account) return null;

        return (
            <div className="validateError validateTop">
                {errors.bank_account.message || "กรอกเลขบัญชีธนาคารให้ถูกต้อง"}
            </div>
        );
    };

    const renderErrorBankAccountName = () => {
        if (errors.bank_account_name) {
            return (
                <div className="validateError validateTop">
                    {errors.bank_account_name.message || "กรุณากรอกชื่อ - นามสกุลให้ถูกต้อง"}
                </div>
            );
        }

        // ไม่มี error → แสดงข้อความช่วยอธิบาย
        return (
            <div className="validateInfo left validateTop">
                กรอกชื่อ-สกุล ไม่ต้องกรอกคำนำหน้า และไม่ต้องใส่เครื่องหมาย (-)
            </div>
        );
    };

    const reloadHomeData = () => {
        loadHomeData().then((data) => {
            if (data.code === 0 || data.code === 200) {
                // if (data.result.banks === null) {
                //     openBox();
                // }

                dispatch(setHomeData(data.result));
            }
        });
    }

    return (
        <>
            <SnackbarSet ref={childRef} />

            {/* ซ่อนฟอร์มไว้แทนการถอดออก เพื่อคงค่าที่กรอกไว้ตอนสลับไปหน้า OTP */}
            <div style={{ display: showOtp ? "none" : "block" }}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container>
                    <Grid item xs={12}>
                        <br />
                        <div className={formStyles.inputPaddingRegisterTop}>
                            <div className={formStyles.inputTextLabel}>
                                เบอร์โทรศัพท์*
                            </div>
                            <Grid container alignItems="center" spacing={1} className={[formStyles.inputStyles, "center"].join(" ")} >
                                <Grid item xs={2}>
                                    <PhoneIcon className={formStyles.iconSize} />
                                </Grid>
                                <Grid item xs={10}>
                                    {/* <input id="register_phone" type="tel" defaultValue={props.register.phone} className={[formStyles.inputStyles, "kanit"].join(" ")} placeholder="เบอร์โทรศัพท์" {...register("phone", { required: true, maxLength: 10, pattern: /^[0-9]+$/i })} onKeyPress={(event) => validateInput(event, "number")} maxLength="10" required /> */}
                                    <input
                                        id="register_phone"
                                        type="tel"
                                        inputMode="numeric"
                                        className={[formStyles.inputStyles, "kanit"].join(" ")}
                                        placeholder="เบอร์โทรศัพท์"
                                        maxLength={10}
                                        {...register("phone", {
                                            required: "กรุณากรอกเบอร์โทรศัพท์",
                                            minLength: {
                                                value: 10,
                                                message:
                                                    "กรุณากรอกเบอร์โทรศัพท์ให้ครบ 10 หลัก"
                                            },
                                            maxLength: {
                                                value: 10,
                                                message:
                                                    "กรุณากรอกเบอร์โทรศัพท์ไม่เกิน 10 หลัก"
                                            },
                                            pattern: {
                                                value: /^[0-9]+$/,
                                                message:
                                                    "เบอร์โทรศัพท์ต้องเป็นตัวเลขเท่านั้น (0–9)"
                                            }
                                        })}
                                        onKeyDown={(event) =>
                                            validateInput(event, "number")
                                        }
                                        onPaste={(event) =>
                                            validateInput(event, "number")
                                        }
                                    />
                                </Grid>
                            </Grid>
                            {
                                renderErrorPhone()
                            }
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <div className={formStyles.inputPaddingRegisterTop}>
                            <div className={formStyles.inputTextLabel}>
                                กรุณาเลือกธนาคาร
                            </div>
                            <Grid container spacing={1}>
                                {
                                    props?.bankList !== undefined && (
                                        props.bankList !== null && (
                                            props.bankList.map((item, index) => (
                                                <Grid key={index} item xs={2}>
                                                    <img className={[formStyles.imgBank, selectBank === item.bank_switfcode ? "" : formStyles.imgBankNotSelect].join(" ")} src={item.bank_img} alt="bank list" onClick={() => clickBank(item.bank_switfcode)} />
                                                </Grid>
                                            ))
                                        )
                                    )
                                }
                            </Grid>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <div className={formStyles.inputPaddingTop}>
                            <div className={formStyles.inputTextLabel}>
                                เลขบัญชีธนาคาร*
                            </div>
                            <Grid container alignItems="center" spacing={1} className={[formStyles.inputStyles, "center"].join(" ")} >
                                <Grid item xs={2}>
                                    <AccountBalanceIcon className={formStyles.iconSize} />
                                </Grid>
                                <Grid item xs={10}>
                                    {/* <input type="text" defaultValue={props.register.bank_account} className={[formStyles.inputStyles, "kanit"].join(" ")} placeholder="กรอกเลขบัญชีธนาคาร" {...register("bank_account", { required: true, maxLength: 13, minLength: 10, pattern: /^[0-9]+$/i })} onKeyPress={(event) => validateInput(event, "number")} maxLength="13" required /> */}
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        className={[formStyles.inputStyles, "kanit"].join(" ")}
                                        placeholder="กรอกเลขบัญชีธนาคาร"
                                        maxLength={13}
                                        {...register("bank_account", {
                                            required:
                                                "กรุณากรอกเลขบัญชีธนาคาร",
                                            minLength: {
                                                value: 10,
                                                message:
                                                    "กรุณากรอกเลขบัญชีธนาคารอย่างน้อย 10 หลัก"
                                            },
                                            maxLength: {
                                                value: 13,
                                                message:
                                                    "เลขบัญชีธนาคารต้องไม่เกิน 13 หลัก"
                                            },
                                            pattern: {
                                                value: /^[0-9]+$/,
                                                message:
                                                    "เลขบัญชีธนาคารต้องเป็นตัวเลขเท่านั้น (0–9)"
                                            }
                                        })}
                                        onKeyDown={(event) =>
                                            validateInput(event, "number")
                                        }
                                        onPaste={(event) =>
                                            validateInput(event, "number")
                                        }
                                    />
                                </Grid>
                            </Grid>
                            {
                                renderErrorBankAccount()
                            }
                        </div>
                    </Grid>
                    {
                        (showAccName || !showAccName) && (
                            <Grid item xs={12}>
                                <div className={formStyles.inputPaddingTop}>
                                    <div className={formStyles.inputTextLabel}>
                                        ชื่อ - นามสกุล*
                                    </div>
                                    <Grid container alignItems="center" spacing={1} className={[formStyles.inputStyles, "center"].join(" ")} >
                                        <Grid item xs={2}>
                                            <PersonIcon className={formStyles.iconSize} />
                                        </Grid>
                                        <Grid item xs={10}>
                                            {/* <input type="text" defaultValue={props.register.bank_account_name} className={[formStyles.inputStyles, "kanit"].join(" ")} placeholder="ชื่อ - นามสกุล" {...register("bank_account_name", { required: true, maxLength: 50, pattern: /^[a-zA-Zก-๙ ]+$/i })} onKeyPress={(event) => validateInput(event, "")} maxLength="50" required /> */}
                                            <input
                                                type="text"
                                                className={[formStyles.inputStyles, "kanit"].join(" ")}
                                                placeholder="ชื่อ-สกุล"
                                                maxLength={100}
                                                {...register("bank_account_name", {
                                                    required:
                                                        "กรุณากรอกชื่อ - นามสกุล",
                                                    minLength: {
                                                        value: 2,
                                                        message:
                                                            "กรุณากรอกชื่อ - นามสกุลให้ถูกต้อง"
                                                    },
                                                    maxLength: {
                                                        value: 100,
                                                        message:
                                                            "ชื่อ - นามสกุลต้องไม่เกิน 100 ตัวอักษร"
                                                    },
                                                    pattern: {
                                                        value: /^[a-zA-Zก-๙ ]+$/i,
                                                        message:
                                                            "ชื่อ - นามสกุลต้องเป็นตัวอักษรไทยหรืออังกฤษเท่านั้น"
                                                    }
                                                })}
                                                onKeyDown={(event) =>
                                                    validateInput(event, "")
                                                }
                                                onPaste={(event) =>
                                                    validateInput(event, "")
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                    {
                                        renderErrorBankAccountName()
                                    }
                                </div>
                            </Grid>
                        )
                    }
                </Grid>

                <div className={[formStyles.inputPaddingTop, "center"].join(" ")} style={{ marginTop: "20px" }}>
                    {/* ยังไม่พ้น cooldown 60 วิ ยิงไปก็โดน code 93 — บอกเวลาที่เหลือแทนการปล่อยให้กดแล้วเด้ง error */}
                    <button id="register_button" type="submit" className={[buttonStyles.btnRedirect, buttonStyles.btnRedirectComponent, formStyles.otpButton].join(" ")} style={{ display: "inline-block" }} disabled={cooldownLeft > 0}>
                        <div style={{ padding: "4px 0px" }}>
                            {
                                cooldownLeft > 0 ?
                                    `ขอรหัสใหม่ได้ในอีก ${cooldownLeft} วินาที`
                                    :
                                    "สมัครสมาชิก"
                            }
                        </div>
                    </button>
                </div>
            </form>
            </div>

            {
                showOtp && (
                    <OtpVerifyApp
                        phone={otpInfo?.phone}
                        refCode={otpInfo?.ref}
                        expireAt={otpInfo?.expireAt}
                        lastSentAt={otpInfo?.lastSentAt}
                        resendCount={resendCount}
                        maxResend={MAX_RESEND}
                        wrongCount={wrongCount}
                        maxWrong={MAX_WRONG}
                        clearSignal={clearSignal}
                        onSubmitOtp={onSubmitOtp}
                        onResend={onResend}
                        onExhausted={onExhausted}
                    />
                )
            }
        </>
    );
}